// src/context/brandVoice/validateField.js — client mirror of the backend's field rules
// (brand_guidelines_edit.py `_validate_field` / `_row_error`). ⛔ The backend's messages are
// English prose and are never shown: every problem here is a KEY into brandVoice.validation.*.
// Run on the Italian value, and on the English value of the three bucket-D fields.
// The params are the BACKEND's own coordinates (row number, claims category, and the record
// key as `name`) so a client check and a 422 land under the same field; this module stays
// language-free — `FieldRow` is where a key becomes a word.
//
// ⚠️ `record_list`'s allowed set: the backend allows the FILE's key order, which for
// `terminology.preferred` also accepts `instead_of`. The editor only ever sends
// `record_fields` keys, and a stored record the AI wrote with `instead_of` is folded into
// `note` by the read, so it never reaches the editor — keeping this strict is the safe side.
import { CLAIM_CATEGORIES } from '../brandIdentityApi.js';
import { PAIR_SEPARATOR } from './wireShape.js';

const blank = (v) => typeof v !== 'string' || !v.trim();
const absent = (v) => v == null || v === '';

// ⭐ v187: a record row whose values are ALL empty is dropped by `toWire` (never sent), so it
// is never a problem. A row with only an optional value is not blank: its missing first key
// is the ordinary `rowRequired`.
const isBlankRecord = (r) => !!r && typeof r === 'object' && !Array.isArray(r)
  && Object.values(r).every((v) => v == null || (typeof v === 'string' && !v.trim()));

// The backend collapses every whitespace run to one space before storing, so a tab or a
// pasted newline after a colon in the NAME would be stored as the separator and move the
// boundary on the next load: the name is checked in its collapsed form.
const collapse = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

// One Principi row (decision 3: checked ONLY on save). A string entry is the stored shape
// and keeps the backend's own rule; a row with BOTH boxes blank is dropped by `toWire`
// (decision A), so it is never sent and never a problem.
function pairProblems(entry, row) {
  if (typeof entry === 'string') return blank(entry) ? [{ key: 'emptyEntry', params: { row } }] : [];
  const name = collapse(entry?.name);
  const description = collapse(entry?.description);
  if (!name && !description) return [];
  if (!name) return [{ key: 'pairNameRequired', params: { row } }];
  if (name.includes(PAIR_SEPARATOR)) return [{ key: 'pairNameSeparator', params: { row } }];
  return [];
}

function rowProblems(row, required, allowed, where) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return [{ key: 'rowShape', params: where }];
  const out = [];
  // ⛔ ONE reason per key. The backend returns at the FIRST problem of a row, so a required
  // key holding a number (`"value": 12`) is "this needs text" — reporting it a second time
  // as an untyped optional would print two sentences about one input.
  const flagged = new Set();
  required.forEach((name) => {
    if (blank(row[name])) { out.push({ key: 'rowRequired', params: { ...where, name } }); flagged.add(name); }
  });
  Object.entries(row).forEach(([name, v]) => {
    if (absent(v) || flagged.has(name)) return;
    if (!allowed.includes(name)) out.push({ key: 'rowUnknown', params: { ...where, name } });
    else if (typeof v !== 'string') out.push({ key: 'rowNotText', params: { ...where, name } });
  });
  return out;
}

/** @returns {Array<{key: string, params?: object}>} — empty when valid. */
export function validateField(field, value) {
  switch (field.widget) {
    case 'scalar':
      if (typeof value !== 'string' && value != null) return [{ key: 'notText' }];
      return field.required && blank(value) ? [{ key: 'required' }] : [];
    case 'str_list':
      // ⭐ v186: a blank entry is DROPPED by `toWire` (decision 5), so it is never sent and
      // never a problem; only the shape is checked.
      return Array.isArray(value) ? [] : [{ key: 'shape' }];
    case 'pair_list': {
      if (!Array.isArray(value)) return [{ key: 'shape' }];
      return value.flatMap((entry, i) => pairProblems(entry, i + 1));
    }
    case 'record_list': {
      if (!Array.isArray(value)) return [{ key: 'shape' }];
      const fields = field.record_fields || [];
      return value.flatMap((r, i) => (isBlankRecord(r)
        ? [] : rowProblems(r, fields.slice(0, 1), fields, { row: i + 1 })));
    }
    case 'claims': {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return [{ key: 'shape' }];
      const spec = field.claim_fields || {};
      return Object.entries(value).flatMap(([cat, rows]) => {
        if (!CLAIM_CATEGORIES.includes(cat)) return [{ key: 'unknownCategory', params: { category: cat } }];
        if (!Array.isArray(rows)) return [{ key: 'shape' }];
        const s = spec[cat] || { required: [], optional: [], passthrough: [] };
        const allowed = [...s.required, ...s.optional, ...(s.passthrough || [])];
        // ⭐ v188: a fully blank row is dropped by `toWire` (never sent), so it is no problem.
        return rows.flatMap((r, i) => (isBlankRecord(r)
          ? [] : rowProblems(r, s.required, allowed, { category: cat, row: i + 1 })));
      });
    }
    default: return [];
  }
}
